/**
 * Retry policy for throttled (HTTP 429) requests (#1747 D4, D11, D12).
 *
 * React Query calls `retry(failureCount, error)` and `retryDelay(failureCount,
 * error)` with the 0-based count of failures *before* this one, so the first
 * failure sees `failureCount === 0`.
 *
 * - Suite default (every query outside Contacts): a 429 whose wait is ≤60s is
 *   retried exactly once, after the wait; a longer wait is not retried, so the
 *   page shows its normal error at once. Other errors keep `retry: 1`.
 * - Contacts queries: a 429 is retried without limit while the query is
 *   mounted (React Query stops retrying once the last observer unmounts).
 * - Contacts mutations: a 429 is resent after the wait; other errors are not
 *   retried (the suite default for mutations is `retry: 0`).
 * - A 429 is never retried before its `Retry-After` has elapsed.
 */

import { isThrottled, throttleRemainingMs, throttleWaitMs } from "@weco/suite-client";

/** Longest wait the suite default will sit through before retrying once. */
export const SUITE_THROTTLE_MAX_WAIT_MS = 60_000;

/** React Query's own default backoff, kept for non-429 failures. */
function defaultRetryDelay(failureCount: number): number {
  return Math.min(1000 * 2 ** failureCount, 30_000);
}

export function suiteQueryRetry(failureCount: number, error: unknown): boolean {
  if (isThrottled(error)) {
    return failureCount < 1 && throttleWaitMs(error, failureCount) <= SUITE_THROTTLE_MAX_WAIT_MS;
  }
  return failureCount < 1;
}

/** The remaining throttle wait for a 429 (never earlier), else React Query's default. */
export function throttleAwareRetryDelay(failureCount: number, error: unknown): number {
  if (isThrottled(error)) return throttleRemainingMs(error, failureCount);
  return defaultRetryDelay(failureCount);
}

export function contactsQueryRetry(failureCount: number, error: unknown): boolean {
  if (isThrottled(error)) return true;
  return failureCount < 1;
}

export function contactsMutationRetry(_failureCount: number, error: unknown): boolean {
  return isThrottled(error);
}

/** Spread into every Contacts `useQuery`. */
export const contactsThrottleRetry = {
  retry: contactsQueryRetry,
  retryDelay: throttleAwareRetryDelay,
} as const;

/** Spread into every Contacts `useMutation`. */
export const contactsMutationThrottleRetry = {
  retry: contactsMutationRetry,
  retryDelay: throttleAwareRetryDelay,
} as const;
